import { forwardRef, useRef, useEffect } from "react";
import { Textarea } from "@/app/common/common-components/ui/Textarea";

// Custom textarea component that supports cursor position insertion
interface CursorTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  onInsertTextAtCursor?: (insertFn: (text: string) => void) => void;
  className?: string;
}

const CursorTextarea = forwardRef<HTMLTextAreaElement, CursorTextareaProps>(
  ({ value, onChange, placeholder, disabled, maxLength, onInsertTextAtCursor, className }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const insertTextAtCursor = (text: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.substring(0, start) + text + value.substring(end);

      onChange(newValue);

      // Set cursor position after inserted text
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    };

    // Expose the insertTextAtCursor method to parent
    useEffect(() => {
      if (onInsertTextAtCursor) {
        onInsertTextAtCursor(insertTextAtCursor);
      }
    }, [onInsertTextAtCursor]);

    return (
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={className || "tw-min-h-[100px]"}
      />
    );
  }
);

CursorTextarea.displayName = "CursorTextarea";

export default CursorTextarea;
